import {
  Divider,
  FormControl,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SubActionButton from "@quest_editor/components/Buttons/SubActionButton";
import { useTranslation } from "react-i18next";
import TablePagination from "@quest_editor/components/TablePagination";
import SectionHeader from "@quest_editor/components/SectionHeader";
import { AuditLogOperationValue } from "@quest_editor/constants";

export type AuditLogRow = {
  id: string;
  domain: string;
  account: string;
  promotionId: string;
  promotionName: string;
  operation: AuditLogOperationValue;
  datetime: string;
};

export type AuditLogFilters = {
  domain: string;
  account: string;
  promotion: string;
  dateFrom: string;
  dateTo: string;
};

type TablePanelProps = {
  pageRows: AuditLogRow[];
  filteredTotal: number;
  filters: AuditLogFilters;
  onFilterChange: (key: keyof AuditLogFilters, value: string) => void;
  onSearch: () => void;
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
};

const TablePanel = ({
  pageRows,
  filteredTotal,
  filters,
  onFilterChange,
  onSearch,
  page,
  pageCount,
  onPageChange,
}: TablePanelProps) => {
  const { t } = useTranslation("questEditor");

  const getOperationLabel = (operation: AuditLogOperationValue): string => {
    return t(`audit_log.table.operation.${operation}`);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        minHeight: 0,
        height: "100%",
      }}
    >
      <SectionHeader title={t("audit_log.table.title")} />

      <Stack direction="row" spacing={2} px={2} py={2} alignItems="center">
        <FormControl>
          <TextField
            variant="outlined"
            size="small"
            label={t("audit_log.table.filter.domain.label")}
            value={filters.domain}
            onChange={(e) => onFilterChange("domain", e.target.value)}
          />
        </FormControl>
        <FormControl>
          <TextField
            variant="outlined"
            size="small"
            label={t("audit_log.table.filter.account.label")}
            value={filters.account}
            onChange={(e) => onFilterChange("account", e.target.value)}
          />
        </FormControl>
        <FormControl>
          <TextField
            variant="outlined"
            size="small"
            label={t("audit_log.table.filter.promotion.label")}
            value={filters.promotion}
            onChange={(e) => onFilterChange("promotion", e.target.value)}
          />
        </FormControl>
        <FormControl>
          <TextField
            variant="outlined"
            size="small"
            type="date"
            label={t("audit_log.table.filter.date_from.label")}
            value={filters.dateFrom}
            onChange={(e) => onFilterChange("dateFrom", e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </FormControl>
        <Typography>～</Typography>
        <FormControl>
          <TextField
            variant="outlined"
            size="small"
            type="date"
            label={t("audit_log.table.filter.date_to.label")}
            value={filters.dateTo}
            onChange={(e) => onFilterChange("dateTo", e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </FormControl>
        <SubActionButton onClick={onSearch}>
          {t("audit_log.table.button.search")}
        </SubActionButton>
      </Stack>

      <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t("audit_log.table.header.domain")}</TableCell>
              <TableCell>{t("audit_log.table.header.account")}</TableCell>
              <TableCell>{t("audit_log.table.header.id")}</TableCell>
              <TableCell>{t("audit_log.table.header.promotion_name")}</TableCell>
              <TableCell>{t("audit_log.table.header.operation")}</TableCell>
              <TableCell>{t("audit_log.table.header.datetime")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary" py={4}>
                    {t("common.label.no_data")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.domain}</TableCell>
                  <TableCell>{row.account}</TableCell>
                  <TableCell>{row.promotionId}</TableCell>
                  <TableCell>{row.promotionName}</TableCell>
                  <TableCell>{getOperationLabel(row.operation)}</TableCell>
                  <TableCell>{row.datetime}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider />

      <TablePagination
        total={filteredTotal}
        page={page}
        setPage={onPageChange}
        count={pageCount}
      />
    </Paper>
  );
};
export default TablePanel;
